import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  Box,
  CircularProgress,
  IconButton,
  makeStyles,
  Tab,
  Tabs,
  Typography,
  // Slider,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '../../icons/SaveIcon';
import {
  COLOR_BACKGROUND_WARM,
  COLOR_BORDER,
  COLOR_MIDDLE_GRAY,
  COLOR_ORANGE,
  COLOR_BLUE,
  COLOR_WHITE,
  COLOR_TEXT,
} from '../../styles/colors';
import { PlayerEvaluationSchema, ProgramEvaluationSchema, PlayerEvaluationDataResponse } from '../../types/Evaluation';
import {
  buildDefaultValues,
  getLastEditDateFromCustomField,
  transformApiResponseToFormValues,
  transformFormDataToPlayerEvaluationData,
} from '../../services/evaluation-helpers';
import { savePlayerProfile } from '../../services/evaluation-api';
import TabsRenderer from './TabsRenderer';
import Player from '../../types/Player';
import Toast, { ToastType } from '../../atoms/Toast';
import Team from '../../types/Team';
import DefaultEvaluationTab from './DefaultEvaluationTab';
import { State } from '../../redux/reducers';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    backgroundColor: COLOR_WHITE,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
  },
  evaluationReportDrawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${COLOR_BORDER}`,
  },
  evaluationReportDrawerHeaderTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 700,
  },
  evaluationReportDrawerHeaderCloseButton: {
    width: 26,
    height: 26,
    cursor: 'pointer',
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginRight: theme.spacing(5),
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    textDecoration: 'underline',
    color: COLOR_BLUE,
    cursor: 'pointer',

    '&:hover': {
      color: COLOR_ORANGE,
    },

    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1),
      marginRight: theme.spacing(2),
    },
  },
  saveButtonIcon: {
    width: 22,
    height: 22,
    color: COLOR_TEXT,
  },
  tabsRoot: {
    borderBottom: `2px solid ${COLOR_BORDER}`,
  },
  tabsIndicator: {
    height: 3,
    backgroundColor: COLOR_ORANGE,
  },
  tabRoot: {
    textTransform: 'none',
    minWidth: 0,
    minHeight: 60,
    padding: theme.spacing(1.5, 3),
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 600,
  },
  tabSelected: {
    fontWeight: 700,
  },
  shimmerContainer: {
    padding: theme.spacing(2),
  },
  shimmerTabs: {
    height: 40,
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    background: 'linear-gradient(90deg, #f1f1f1 25%, #e6e6e6 37%, #f1f1f1 63%)',
    backgroundSize: '400% 100%',
    animation: '$shimmer 1.4s ease infinite',
  },
  shimmerCard: {
    height: 120,
    borderRadius: 10,
    marginBottom: theme.spacing(2),
    background: 'linear-gradient(90deg, #f1f1f1 25%, #e6e6e6 37%, #f1f1f1 63%)',
    backgroundSize: '400% 100%',
    animation: '$shimmer 1.4s ease infinite',
  },
  shimmerRow: {
    height: 46,
    borderRadius: 8,
    marginBottom: theme.spacing(1.5),
    background: 'linear-gradient(90deg, #f1f1f1 25%, #e6e6e6 37%, #f1f1f1 63%)',
    backgroundSize: '400% 100%',
    animation: '$shimmer 1.4s ease infinite',
  },
  '@keyframes shimmer': {
    '0%': {
      backgroundPosition: '100% 0',
    },
    '100%': {
      backgroundPosition: '0 0',
    },
  },
}), { name: 'EvaluationDrawerContent' });

interface EvaluationDrawerContentProps {
  programEvaluationDataSchema?: ProgramEvaluationSchema;
  playerEvaluationData?: PlayerEvaluationDataResponse | null;
  isLoading?: boolean;
  player?: Player;
  onClose: () => void;
  evaluationTeam: Team | null | undefined;
  onRefetchPlayerEvaluationData?: () => Promise<any>;
}

export default function EvaluationDrawerContent(props: EvaluationDrawerContentProps) {
  const {
    programEvaluationDataSchema,
    playerEvaluationData,
    isLoading = false,
    player,
    onClose,
    evaluationTeam,
    onRefetchPlayerEvaluationData,
  } = props;
  const classes = useStyles();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState<ToastType>(ToastType.INFO);
  const [toastMessage, setToastMessage] = useState('');
  const playerEvaluationValidationIsAll = useSelector(
    (state: State) => state.userSettings.playerEvaluationValidationIsAll,
  );

  const schema: PlayerEvaluationSchema | undefined = useMemo(
    () => transformApiResponseToFormValues(programEvaluationDataSchema || null, playerEvaluationData || null),
    [programEvaluationDataSchema, playerEvaluationData],
  );
  
  const defaultValues = useMemo(
    () => (schema
      ? {
        ...buildDefaultValues(schema),
        evalScore: schema.playerEvaluationScore,
        evalCode: schema.playerEvaluationCode,
        evalColor: schema.evaluationColor,
        playerDisplayMode: playerEvaluationValidationIsAll ? 'all' : 'none',
        playerDisplaySkip: schema.playerDisplay === 'skip',
      }
      : {}),
    [schema, playerEvaluationValidationIsAll],
  );

  const fieldLastEditDateMap = useMemo(() => {
    const customFields = playerEvaluationData?.customFields || {};
    return Object.keys(customFields).reduce<Record<string, string | undefined>>((acc, fieldName) => {
      acc[fieldName] = getLastEditDateFromCustomField(customFields[fieldName]);
      return acc;
    }, {});
  }, [playerEvaluationData]);

  const getLastEditDateForField = useCallback((fieldName?: string) => {
    if (!fieldName) return undefined;
    return fieldLastEditDateMap[fieldName];
  }, [fieldLastEditDateMap]);

  const evalColorPossibleValues = useMemo(() => {
    const evalColorField = programEvaluationDataSchema?.customFields?.find(field => field.fieldName === 'evalColor');
    return evalColorField?.possibleValues || undefined;
  }, [programEvaluationDataSchema]);

  const defaultTabIndex = useMemo(() => {
    return schema?.customTabs.findIndex(tab => String(tab.id) === '1') ?? 0;
  }, [schema]);

  const defaultTab = useMemo(() => {
    if (!schema || defaultTabIndex < 0) return undefined;
    return schema.customTabs[defaultTabIndex];
  }, [schema, defaultTabIndex]);

  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSave = useCallback(
    async (formData: Record<string, any>) => {
      if (!schema) return;
      if (!player?.id) return;
      setIsSaving(true);
      try {
        const payload = transformFormDataToPlayerEvaluationData(
          formData,
          schema,
          playerEvaluationData,
        );
        payload.customFields.evalScore = { value: formData.evalScore };
        payload.customFields.evalCode = { value: formData.evalCode };
        payload.customFields.evalColor = { value: formData.evalColor };
        const nextPlayerDisplay = formData.playerDisplayMode === 'none'
          ? 'none'
          : (formData.playerDisplaySkip ? 'skip' : 'all');
        payload.customFields.playerDisplay = { value: nextPlayerDisplay };

        const result = await savePlayerProfile(payload, player.id);
        if (result.success) {
          if (onRefetchPlayerEvaluationData) {
            await onRefetchPlayerEvaluationData();
          }
          console.log('Player profile saved successfully');
          setToastMessage('Evaluation saved successfully');
          setToastType(ToastType.SUCCESS);
          setToastVisible(true);
        } else {
          setToastMessage('Failed to save evaluation');
          setToastType(ToastType.ERROR);
          setToastVisible(true);
        }
      } catch (error) {
        console.error('Failed to save player profile:', error);
        setToastMessage('Failed to save evaluation');
        setToastType(ToastType.ERROR);
        setToastVisible(true);
      } finally {
        setIsSaving(false);
      }
    },
    [schema, playerEvaluationData, player?.id, onRefetchPlayerEvaluationData],
  );

  if (!schema) {
    return null;
  }

  const showShimmer = Boolean(programEvaluationDataSchema && (isLoading || !playerEvaluationData));

  return (
    <>
      <form
        className={classes.root}
        onSubmit={handleSubmit(onSave)}
        noValidate
      >
        {/* Header */}
        <Box className={classes.evaluationReportDrawerHeader}>
          <div className={classes.evaluationReportDrawerHeaderTitle}>
            <img
              src={evaluationTeam?.logo247 || evaluationTeam?.logoESPN || ''}
              alt={evaluationTeam?.name || 'Team Logo'}
              width={20}
              height={20}
            />

            Evaluation
          </div>

          <Box display="flex" alignItems="center">
            <div
              className={classes.saveButton}
              onClick={handleSubmit(onSave)}
            >
              {isSaving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveIcon className={classes.saveButtonIcon} />
              )}
              
              {isSaving ? 'Saving...' : 'Save All'}
            </div>

            <IconButton size="small" onClick={onClose}>
              <CloseIcon className={classes.evaluationReportDrawerHeaderCloseButton} />
            </IconButton>
          </Box>
        </Box>

        {showShimmer ? (
          <div className={classes.shimmerContainer}>
            <div className={classes.shimmerTabs} />
            <div className={classes.shimmerCard} />
            <div className={classes.shimmerRow} />
            <div className={classes.shimmerRow} />
            <div className={classes.shimmerRow} />
          </div>
        ) : (
          <>
        {/* Tabs — dynamically rendered from schema */}
        <Tabs
          className={classes.tabsRoot}
          value={selectedTab}
          classes={{ indicator: classes.tabsIndicator }}
          onChange={(_event: any, value: number) => setSelectedTab(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {schema.customTabs.map((tab, idx) => (
            <Tab
              key={tab.name}
              label={tab.name}
              value={idx}
              classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
            />
          ))}
        </Tabs>

        {/* Scrollable tab content */}
        <div className={classes.scrollableContent}>
          {/* Score card shown on first tab */}
          {selectedTab === defaultTabIndex && defaultTab && (
            <DefaultEvaluationTab
              schema={schema}
              defaultTab={defaultTab}
              defaultTabIndex={defaultTabIndex}
              control={control}
              setValue={setValue}
              playerId={player?.id}
              evalColorPossibleValues={evalColorPossibleValues}
              getLastEditDateForField={getLastEditDateForField}
            />
          )}

          {/* Dynamic fields for the selected tab */}
          {schema.customTabs
            .filter((_tab, tabIdx) => tabIdx !== defaultTabIndex)
            .map((tab, tabIdx) => {
              const actualTabIndex = schema.customTabs.findIndex(candidate => candidate.name === tab.name);
              return (
            <div
              key={tab.name}
              role="tabpanel"
              hidden={selectedTab !== actualTabIndex}
            >
              {selectedTab === actualTabIndex && (
                <TabsRenderer
                  tab={tab}
                  tabIndex={actualTabIndex}
                  control={control}
                  getLastEditDateForField={getLastEditDateForField}
                />
              )}
            </div>
              );
            })}
        </div>
          </>
        )}
      </form>
      <Toast
        visible={toastVisible}
        type={toastType}
        onHide={() => setToastVisible(false)}
      >
        {toastMessage}
      </Toast>
    </>
    
  );
}